/**
 * Pure-function side-channel-suppression policy for message requests.
 *
 * Signal's threat model for message requests includes the sender learning,
 * from a side channel, that the recipient has the app open / has read the
 * message — *before* the recipient has chosen to engage. Anything we send
 * back to the sender automatically (delivery receipts, read receipts, typing
 * indicators) leaks that signal.
 *
 * So while a peer is 'requested' or 'blocked', every automatic outbound
 * signal is suppressed. Once the user has explicitly accepted, normal
 * behaviour resumes. A null record (peer is a contact and has no message
 * request entry) is treated as 'accepted' for policy purposes — contacts
 * always get receipts and typing.
 *
 * All decisions are pure functions of the record's state, so the self-test
 * can exhaustively cover every (state × decision) pair without touching
 * IndexedDB or shared singletons.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/MessageRequestPolicy.swift` exactly.
 */

import type { MessageRequestRecord } from "./MessageRequest";

export const MessageRequestPolicy = {
  /**
   * Whether to send a delivery receipt to this peer right now.
   * 'requested' and 'blocked' → false (don't reveal "I received it");
   * null and 'accepted' → true.
   *
   * Callers: wire into `buildDeliveryReceipt(...)` — return null when this
   * returns false.
   */
  shouldSendDeliveryReceipt(record: MessageRequestRecord | null): boolean {
    const state = record?.state;
    if (state === "requested" || state === "blocked") return false;
    return true; // null (contact/no-record) or 'accepted'
  },

  /**
   * Whether to send a read receipt. Same gating as delivery receipts — the
   * global "user has read receipts enabled" toggle still applies on top of
   * this and is the caller's responsibility.
   *
   * Callers: wire into `buildReadReceipt(...)` — return null when this
   * returns false.
   */
  shouldSendReadReceipt(record: MessageRequestRecord | null): boolean {
    const state = record?.state;
    if (state === "requested" || state === "blocked") return false;
    return true;
  },

  /**
   * Whether to ship a typing indicator to this peer. We treat typing the
   * same as receipts: anything that proves the user is at the keyboard is
   * suppressed until the request is accepted.
   *
   * Callers: consult this in `canSendTypingIndicator(to:)` before firing
   * typing-indicator pushes.
   */
  shouldSendTypingIndicator(record: MessageRequestRecord | null): boolean {
    const state = record?.state;
    if (state === "requested" || state === "blocked") return false;
    return true;
  },

  /**
   * Whether the chat-list UI should render messages from this peer in the
   * main feed. 'requested' belongs in the requests-inbox view; 'blocked' is
   * hidden everywhere.
   */
  shouldRender(record: MessageRequestRecord | null): boolean {
    const state = record?.state;
    if (state === "requested" || state === "blocked") return false;
    return true;
  },

  /**
   * Whether to surface this conversation in the message-requests inbox. Only
   * 'requested' qualifies — accepted contacts live in the main chat list,
   * blocked peers don't surface anywhere.
   */
  shouldSurfaceAsRequest(record: MessageRequestRecord | null): boolean {
    return record?.state === "requested";
  },
} as const;
